import { useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import type { Integration } from "../../management/types";
import { cancelCrmExportThunk, cancelCrmLoadingThunk } from "../store/export-flow-thunks";
import { confirmRowCrmPreviewExportThunk, singleContactCrmExportThunk } from "../store/row-export-thunks";
import { selectCrmToExportContacts, selectExportFlowOrigin, selectExportFlowPhase } from "../store/selectors";
import type { ContactSearchEntry, CRMEntity } from "../types/common";

import { useRevealBeforeExport } from "./useRevealBeforeExport";

const DEFAULT_COUNTRIES: readonly number[] = [WORLDWIDE_COUNTRY_ID];

type UseSingleContactCrmExportParams = {
    countries?: readonly number[];
};

type UseSingleContactCrmExportResult = {
    onExportToCRM: (contact: ContactSearchEntry, integration: Integration, as?: CRMEntity) => void;
    isRowMatching: boolean;
    isRowPreviewing: boolean;
    integrationName: Integration | undefined;
    onConfirmCrmExport: () => void;
    onCancelCrmPreview: () => void;
    onCancelCrmLoading: () => void;
    isRevealModalOpen: boolean;
    onConfirmRevealExport: () => void;
    onCloseRevealModal: () => void;
};

const useSingleContactCrmExport = ({
    countries = DEFAULT_COUNTRIES,
}: UseSingleContactCrmExportParams): UseSingleContactCrmExportResult => {
    const dispatch = useDispatch();
    const integrationName = useSelector(selectCrmToExportContacts);
    const phase = useSelector(selectExportFlowPhase);
    const origin = useSelector(selectExportFlowOrigin);

    const isRowFlow = origin === "row";
    const isRowMatching = isRowFlow && phase.type === "matchingCrmContacts";
    const isRowPreviewing = isRowFlow && phase.type === "previewingCrmContacts";

    const triggerCrmExport = useCallback(
        (contact: ContactSearchEntry, integration: Integration, as: CRMEntity) => {
            void dispatch(
                singleContactCrmExportThunk({
                    contact,
                    integration,
                    exportAs: as,
                    countries,
                }),
            );
        },
        [countries],
    );

    const {
        tryExportOrReveal,
        isRevealModalOpen,
        onConfirmReveal: onConfirmRevealExport,
        onCloseRevealModal,
    } = useRevealBeforeExport(triggerCrmExport);

    const onConfirmCrmExport = useCallback(() => {
        void dispatch(confirmRowCrmPreviewExportThunk(countries));
    }, [countries]);

    const onCancelCrmPreview = useCallback(() => {
        dispatch(cancelCrmExportThunk());
    }, []);

    const onCancelCrmLoading = useCallback(() => {
        dispatch(cancelCrmLoadingThunk());
    }, []);

    return {
        onExportToCRM: tryExportOrReveal,
        isRowMatching,
        isRowPreviewing,
        integrationName,
        onConfirmCrmExport,
        onCancelCrmPreview,
        onCancelCrmLoading,
        isRevealModalOpen,
        onConfirmRevealExport,
        onCloseRevealModal,
    };
};

export default useSingleContactCrmExport;
